import ArrowIcon from "$/lib/icons/ArrowIcon";
import type { IndicatorType } from "$/lib/types/api/market-monitoring/market-monitoring.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import Flexbox from "../../common/Flexbox";

export type GraphIndicatorItemType = {
  title: string;
  baseloadItem: IndicatorType;
  peakloadItem: IndicatorType;
};

type GraphIndicatorItemProps = {
  item: GraphIndicatorItemType;
  unit?: string;
};

type SingleIndicatorProps = {
  item: IndicatorType;
  unit: string;
};

const Indicator = ({ item, unit }: SingleIndicatorProps) => {
  const value = item?.value;
  const differencePercentage = item?.differencePercentage;

  return (
    <Flexbox align="center" justify="center" className="gap-1 p-1">
      <span className="text-xs font-normal">
        {value} {unit}
      </span>

      <Flexbox row align="center" justify="center" className="gap-1">
        <Flexbox
          className={cn("rounded-full p-1", {
            "bg-forest-100": differencePercentage && differencePercentage >= 0,
            "bg-error-100": differencePercentage && differencePercentage < 0,
            "bg-[#BCBCBC1A]": !differencePercentage,
          })}
        >
          <ArrowIcon
            className={cn("h-2 w-2", {
              "-rotate-90 [&_path]:!stroke-success-800":
                differencePercentage && differencePercentage >= 0,
              "rotate-90 [&_path]:!stroke-error-800":
                differencePercentage && differencePercentage < 0,
              "-rotate-90 [&_path]:!stroke-[#BCBCBC]": !differencePercentage,
            })}
          />
        </Flexbox>
        <p
          className={cn("text-sm font-semibold", {
            "text-forest-800":
              differencePercentage && differencePercentage >= 0,
            "text-error-800": differencePercentage && differencePercentage < 0,
            "text-[#BCBCBC]": !differencePercentage,
          })}
        >
          {differencePercentage
            ? `${
                differencePercentage <= 0 ? "+" : "-"
              } ${Math.abs(differencePercentage)}%`
            : ""}
        </p>
      </Flexbox>
    </Flexbox>
  );
};

export const GraphIndicatorItem = ({
  item,
  unit = " €/MWh",
}: GraphIndicatorItemProps) => {
  return (
    <Flexbox
      fullWidth
      fullHeight
      row
      align="center"
      justify="between"
      className={cn("grid grid-cols-3 gap-2 border-b-[1px] border-primary-300")}
    >
      <Flexbox
        fullWidth
        fullHeight
        align="center"
        justify="center"
        className="bg-primary-200 p-1"
      >
        <p className="text-sm font-medium text-black">{item.title}</p>
      </Flexbox>

      <Indicator item={item.baseloadItem} unit={unit} />

      <Indicator item={item.peakloadItem} unit={unit} />
    </Flexbox>
  );
};
